import createHttp from '@/config/http';
import type { AccountProductInfo } from '@/types/account';
import type { LocationData } from '@/types/location';
import type { CommissionableProduct } from '@/types/orderReturns';
import type { SalesRep } from '@/types/reps';
import type { IResponseFetch, ResponseMetaData } from '@/types/service';
import type {
	SharePointDeletePayload,
	SharePointDeleteResponse,
	SharePointFile,
	SharePointListResponse,
	SharePointUploadPayload,
	SharePointUploadResponse,
} from '@/types/sharepoint';
import type { UserActivityTimeline } from '@/types/users';

const http = createHttp(import.meta.env.VITE_COMMON_URL);

// Re-export type
export type { LocationData };

export const getAllLocations = async (
	page = 1,
	limit = 50,
	search = '',
	filterIds = '',
): Promise<{
	items: LocationData[];
	meta: ResponseMetaData;
}> => {
	const response = await http.get<IResponseFetch<LocationData>>(
		'/common/findAllLocations',
		{
			params: {
				page,
				limit,
				search,
				filterIds,
			},
		},
	);
	return {
		items: response.data.data.items,
		meta: response.data.data.metaData,
	};
};

export const getAllSalesReps = async (
	page = 1,
	limit = 50,
	search = '',
	filterIds = '',
): Promise<{
	items: SalesRep[];
	meta: ResponseMetaData;
}> => {
	const response = await http.get<IResponseFetch<SalesRep>>(
		'/common/getSalesRepAccounts',
		{
			params: {
				page,
				limit,
				search,
				filterIds,
			},
		},
	);
	return {
		items: response.data.data.items,
		meta: response.data.data.metaData,
	};
};
/**
 * Uploads a file to SharePoint
 * @param payload - The upload payload containing base64 file data and metadata
 * @returns Promise with the SharePoint response
 */
export const uploadFileToSharePoint = async (
	payload: SharePointUploadPayload,
): Promise<SharePointUploadResponse> => {
	const response = await http.post<SharePointUploadResponse>(
		'/common/sharepoint/upload',
		payload,
	);
	return response.data;
};

/**
 * Deletes a file from SharePoint
 * @param payload - The delete payload containing file metadata
 * @returns Promise with the SharePoint response
 */
export const deleteFileFromSharePoint = async (
	payload: SharePointDeletePayload,
): Promise<SharePointDeleteResponse> => {
	const response = await http.delete<SharePointDeleteResponse>(
		'/common/sharepoint/delete',
		{ data: payload },
	);
	return response.data;
};

/**
 * Gets a list of folders and files from SharePoint
 * @param clinic - The clinic identifier
 * @param folder - Optional folder name to filter by (can be nested like "abc/xyz")
 * @returns Promise with the SharePoint list response
 */
export const getSharePointList = async (
	clinic: string,
	folder?: string,
	sort?: string,
	columnsFilters?: string,
	fileType?: string,
): Promise<SharePointListResponse> => {
	const response = await http.get<SharePointListResponse>(
		'/common/sharepoint/list',
		{
			params: {
				clinic,
				...(folder && { folder }),
				sort,
				columnsFilters,
				fileType,
			},
		},
	);
	return response.data;
};
/**
 * Gets base64-encoded file content for a specific SharePoint file.
 * The backend endpoint returns a NestJS StreamableFile; this helper
 * assumes it is serialized as a base64 string in the HTTP response body.
 */
export const getSharePointFileContent = async (
	clinic: string,
	folderName: string,
	fileName: string,
): Promise<SharePointFile> => {
	const response = await http.get<{ data: SharePointFile }>(
		'/common/sharepoint/file',
		{
			params: {
				clinic,
				folderName,
				fileName,
			},
		},
	);

	return response.data.data;
};
/**
 * Gets unassigned tissues for a specific clinic
 * @param clinicName - The name of the clinic
 * @returns Promise with the unassigned tissues data
 */
export const getUnassignedTissues = async (clinicName: string) => {
	const response = await http.get(
		`/grafts-to-assign/unassigned-tissues?clinics=${encodeURIComponent(clinicName)}`,
	);
	return response.data;
};

export const getProductsByClinicsAndPort = async (
	clinicIds: string[],
	port: string,
): Promise<AccountProductInfo[]> => {
	const response = await http.post(`/products/get-products-by-clinic`, {
		clinicIds,
		port,
	});
	return response.data;
};

export const getCommissionableProdsByQCodes = async (payload: { qcodes: string }): Promise<CommissionableProduct[]> => {
	const response = await http.post(
		`/products/commissionable-qcodes`,
		payload
	);
	return response.data;
};

export const getActivityTimeline = async (userId: string, page?: number, limit?: number): Promise<{
	annotations: UserActivityTimeline[];
	totalRecords: number;
}> => {
	const response = await http.get(`/common/get-user-activity`, {
		params: {
			userId,
			page,
			limit,
		},
	});
	return response.data;
};

export const getActivityFile = async (url: string) => {
	const response = await http.get('/common/get-activity-file', { params: { url }, responseType: 'blob' });
	return response;
}

export const uploadToActivity = async (activityId: string, text: string, files?: Array<{ filename: string; documentbody: string; mimetype: string }>) => {
	const payload: any = {
		activityId,
		text,
	};

	if (files && files.length > 0) {
		payload.files = files;
	}

	const response = await http.post(`/common/upload-activity-file`, payload);
	return response.data;
};